import { defineStore } from "pinia"
import { computed, ref, watch } from "vue"
import { useLocalStorage } from "@vueuse/core"
import { UseRootStore } from "@/model/RootStore"
import {
	SearchContextScope,
	type SearchContext,
	type SearchContextPreset,
	type SearchContextPresetDefinition,
} from "@/graphql"
import { type View, ViewScope } from "@/types"
import * as apiClient from "@/utils/ApiClient"
import { activeFilterFieldCount, normalizeViewFilterDefaults } from "@/views/viewFilterDefaults"
import { apiViewToView, isSameViewState, useSearchContextStore } from "./searchContextStore"
import { useViewStore } from "./viewStore"

const ACTIVE_TABLE_SAVED_VIEWS_STORAGE_KEY = "table-saved-views-active"

const TABLE_VIEW_SCOPES: Record<SearchContextScope, ViewScope> = {
	[SearchContextScope.OpportunitiesTable]: ViewScope.OpportunitiesTable,
	[SearchContextScope.OpportunitiesHuntingTable]: ViewScope.OpportunitiesHuntingTable,
	[SearchContextScope.TransactionsTable]: ViewScope.TransactionsTable,
}

export type PresetView = {
	id: string
	preset: SearchContextPreset
	view: View
}

const TEXT_SEARCH_FIELD = "search"
const NAME_UNIQUE_CONSTRAINT_PREFIX = "idx_search_context_user"

export class DuplicateSavedViewNameError extends Error {
	constructor() {
		super("A saved view with this name already exists")
	}
}

function isSameName(a: string, b: string): boolean {
	return a.trim().toLowerCase() === b.trim().toLowerCase()
}

async function withDuplicateNameError<T>(request: Promise<T>): Promise<T> {
	try {
		return await request
	} catch (error) {
		if (error instanceof Error && error.message.includes(NAME_UNIQUE_CONSTRAINT_PREFIX)) {
			throw new DuplicateSavedViewNameError()
		}
		throw error
	}
}

function transactionTextSearch(view: View): string | null {
	return view.filter?.transaction?.search ?? null
}

function withTransactionTextSearch(view: View, search: string | null): View {
	if (!search && view.filter?.transaction == null) return view
	const { search: _, ...transactionFilter } = view.filter?.transaction ?? {}
	return {
		...view,
		filter: {
			...view.filter,
			transaction: search ? { ...transactionFilter, search } : transactionFilter,
		},
	}
}

function withoutTextSearch(view: View): View {
	return withTransactionTextSearch(view, null)
}

type TableViewSummary = {
	filters: number
	sorts: number
	firstSort: NonNullable<View["order"]>[number] | null
	hasCustomColumns: boolean
}

type SortOption = {
	value: string
	label: string
}

export function summarizeTableView(view: View): TableViewSummary {
	const filter = view.filter ?? {}
	return {
		filters:
			activeFilterFieldCount(filter.opportunity, [TEXT_SEARCH_FIELD]) +
			activeFilterFieldCount(filter.transaction, [TEXT_SEARCH_FIELD]),
		sorts: view.order?.length ?? 0,
		firstSort: view.order?.[0] ?? null,
		hasCustomColumns:
			view.select?.opportunity != null ||
			view.select?.transaction != null ||
			Object.keys(view.columnOverrides ?? {}).length > 0,
	}
}

export const useTableSavedViewStore = defineStore("table-saved-view", () => {
	const rootStore = UseRootStore()
	const viewStore = useViewStore()
	const searchContextStore = useSearchContextStore()
	const projectIdsByScope = ref<Partial<Record<SearchContextScope, string | null>>>({})
	const savedViewsByScope = ref<Partial<Record<SearchContextScope, SearchContext[]>>>({})
	const presetViewsByScope = ref<Partial<Record<SearchContextScope, PresetView[]>>>({})
	const activeIdsByScope = useLocalStorage<Partial<Record<SearchContextScope, string>>>(
		ACTIVE_TABLE_SAVED_VIEWS_STORAGE_KEY,
		{}
	)
	const loading = ref(false)
	const sortLabelsByScope = ref<Partial<Record<SearchContextScope, Record<string, string>>>>({})

	function currentUserProfileId(): string {
		const id = rootStore.currentUser.getInfo()?.id
		if (id == null) throw new Error("Current user id is required")
		return String(id)
	}

	function setSortOptions(scope: SearchContextScope, options: SortOption[]): void {
		sortLabelsByScope.value = {
			...sortLabelsByScope.value,
			[scope]: Object.fromEntries(options.map(option => [option.value, option.label])),
		}
	}

	function sortLabel(scope: SearchContextScope, field: string): string | null {
		return sortLabelsByScope.value[scope]?.[field] ?? null
	}

	function savedViews(scope: SearchContextScope): SearchContext[] {
		return savedViewsByScope.value[scope] ?? []
	}

	function assertUniqueName(scope: SearchContextScope, name: string, exceptId: string | null = null): void {
		const isTaken = savedViews(scope).some(
			searchContext => searchContext.id !== exceptId && isSameName(searchContext.name, name)
		)
		if (isTaken) throw new DuplicateSavedViewNameError()
	}

	function setSavedViews(scope: SearchContextScope, searchContexts: SearchContext[]): void {
		savedViewsByScope.value = { ...savedViewsByScope.value, [scope]: searchContexts }
	}

	function setPresetViews(scope: SearchContextScope, presets: SearchContextPresetDefinition[]): void {
		const presetViews = presets.flatMap(({ preset, views }) => {
			const view = views.find(view => view.scope === TABLE_VIEW_SCOPES[scope])
			return view ? [{ id: `preset-${preset}`, preset, view: apiViewToView(view) }] : []
		})
		presetViewsByScope.value = { ...presetViewsByScope.value, [scope]: presetViews }
	}

	function activeSavedView(scope: SearchContextScope): SearchContext | null {
		const id = activeIdsByScope.value[scope]
		return savedViews(scope).find(searchContext => searchContext.id === id) ?? null
	}

	function presetViews(scope: SearchContextScope): PresetView[] {
		const customizedPresets = new Set(savedViews(scope).map(searchContext => searchContext.preset))
		return (presetViewsByScope.value[scope] ?? []).filter(presetView => !customizedPresets.has(presetView.preset))
	}

	function activePresetView(scope: SearchContextScope): PresetView | null {
		const id = activeIdsByScope.value[scope]
		return presetViews(scope).find(presetView => presetView.id === id) ?? null
	}

	function setActiveId(scope: SearchContextScope, id: string | null): void {
		const { [scope]: _, ...others } = activeIdsByScope.value
		activeIdsByScope.value = id == null ? others : { ...others, [scope]: id }
	}

	function isAppliedOnProject(scope: SearchContextScope): boolean {
		return (
			scope === SearchContextScope.OpportunitiesHuntingTable &&
			projectIdsByScope.value[scope] != null &&
			searchContextStore.isProjectSearchContextLoaded
		)
	}

	function defaultView(scope: SearchContextScope): View {
		const viewScope = TABLE_VIEW_SCOPES[scope]
		return isAppliedOnProject(scope) ? searchContextStore.projectView(viewScope) : { scope: viewScope }
	}

	function currentView(scope: SearchContextScope): View {
		return viewStore.view(TABLE_VIEW_SCOPES[scope]) ?? defaultView(scope)
	}

	function viewOf(scope: SearchContextScope, searchContext: SearchContext): View {
		const view = searchContext.views.find(view => view.scope === TABLE_VIEW_SCOPES[scope])
		return view ? apiViewToView(view) : defaultView(scope)
	}

	function referenceView(scope: SearchContextScope): View {
		const active = activeSavedView(scope)
		if (active) return viewOf(scope, active)
		return activePresetView(scope)?.view ?? defaultView(scope)
	}

	function isSameView(a: View, b: View): boolean {
		return isSameViewState(
			apiClient.toSavedViewInput(normalizeViewFilterDefaults(withoutTextSearch(a))),
			apiClient.toSavedViewInput(normalizeViewFilterDefaults(withoutTextSearch(b)))
		)
	}

	function isModified(scope: SearchContextScope): boolean {
		return !isSameView(currentView(scope), referenceView(scope))
	}

	function applyView(scope: SearchContextScope, view: View): void {
		viewStore.upsertView(withTransactionTextSearch(view, transactionTextSearch(currentView(scope))))
	}

	function currentSavedViewInput(scope: SearchContextScope) {
		return apiClient.toSavedViewInput(withoutTextSearch(currentView(scope)))
	}

	async function fetchSavedViews(scope: SearchContextScope, projectId: string | null): Promise<void> {
		loading.value = true
		projectIdsByScope.value = { ...projectIdsByScope.value, [scope]: projectId }
		try {
			const [searchContexts, presets] = await Promise.all([
				apiClient.fetchSearchContexts(rootStore.userApiToken, {
					userProfileId: currentUserProfileId(),
					projectId,
					scope,
				}),
				apiClient.fetchSearchContextPresets(rootStore.userApiToken, scope),
			])
			setSavedViews(scope, searchContexts)
			setPresetViews(scope, presets)
			reapplyOnProjectView(scope)
		} finally {
			loading.value = false
		}
	}

	function reapplyOnProjectView(scope: SearchContextScope): void {
		const active = activeSavedView(scope)
		if (!active || !isAppliedOnProject(scope)) return
		if (isSameView(currentView(scope), defaultView(scope))) applySavedView(scope, active)
	}

	const huntingScope = SearchContextScope.OpportunitiesHuntingTable
	const isHuntingSavedViewOnProject = computed(
		() => isAppliedOnProject(huntingScope) && activeSavedView(huntingScope) != null
	)

	watch(isHuntingSavedViewOnProject, isPersonal =>
		searchContextStore.setPersonalViewScope(TABLE_VIEW_SCOPES[huntingScope], isPersonal)
	)

	watch(
		() => searchContextStore.searchContext,
		() => reapplyOnProjectView(huntingScope)
	)

	function applySavedView(scope: SearchContextScope, searchContext: SearchContext): void {
		applyView(scope, viewOf(scope, searchContext))
		setActiveId(scope, searchContext.id)
	}

	function applyPresetView(scope: SearchContextScope, presetView: PresetView): void {
		applyView(scope, presetView.view)
		setActiveId(scope, presetView.id)
	}

	function applyDefaultView(scope: SearchContextScope): void {
		applyView(scope, defaultView(scope))
		setActiveId(scope, null)
	}

	function discardChanges(scope: SearchContextScope): void {
		const active = activeSavedView(scope)
		const activePreset = activePresetView(scope)
		if (active) {
			applySavedView(scope, active)
		} else if (activePreset) {
			applyPresetView(scope, activePreset)
		} else {
			applyDefaultView(scope)
		}
	}

	async function insertSavedView(
		scope: SearchContextScope,
		input: Omit<Parameters<typeof apiClient.createSearchContext>[1], "userProfileId" | "scope">
	): Promise<SearchContext> {
		assertUniqueName(scope, input.name)
		const created = await withDuplicateNameError(
			apiClient.createSearchContext(rootStore.userApiToken, {
				...input,
				userProfileId: currentUserProfileId(),
				scope,
			})
		)
		setSavedViews(scope, [created, ...savedViews(scope)])
		return created
	}

	async function createSavedView(
		scope: SearchContextScope,
		name: string,
		projectId: string | null
	): Promise<SearchContext> {
		const created = await insertSavedView(scope, { name, projectId, views: [currentSavedViewInput(scope)] })
		setActiveId(scope, created.id)
		return created
	}

	async function saveActivePresetView(scope: SearchContextScope, presetName: string): Promise<void> {
		const activePreset = activePresetView(scope)
		if (!activePreset) return
		const copy = await insertSavedView(scope, {
			name: presetName,
			preset: activePreset.preset,
			usesPresetName: true,
			views: [currentSavedViewInput(scope)],
		})
		setActiveId(scope, copy.id)
	}

	async function renamePresetView(scope: SearchContextScope, presetView: PresetView, name: string): Promise<void> {
		const wasActive = activePresetView(scope)?.id === presetView.id
		const copy = await insertSavedView(scope, {
			name,
			preset: presetView.preset,
			usesPresetName: false,
			views: [apiClient.toSavedViewInput(withoutTextSearch(presetView.view))],
		})
		if (wasActive) setActiveId(scope, copy.id)
	}

	async function updateSearchContext(
		scope: SearchContextScope,
		id: string,
		input: Parameters<typeof apiClient.updateSearchContext>[2]
	): Promise<SearchContext> {
		const updated = await apiClient.updateSearchContext(rootStore.userApiToken, id, input)
		setSavedViews(
			scope,
			savedViews(scope).map(searchContext => (searchContext.id === updated.id ? updated : searchContext))
		)
		return updated
	}

	async function saveActiveSavedView(scope: SearchContextScope): Promise<void> {
		const active = activeSavedView(scope)
		if (!active) return
		await updateSearchContext(scope, active.id, { views: [currentSavedViewInput(scope)] })
	}

	async function renameSavedView(scope: SearchContextScope, id: string, name: string): Promise<void> {
		assertUniqueName(scope, name, id)
		await withDuplicateNameError(updateSearchContext(scope, id, { name }))
	}

	async function deleteSavedView(scope: SearchContextScope, id: string): Promise<void> {
		const deleted = await apiClient.deleteSearchContext(rootStore.userApiToken, id)
		if (!deleted) return
		setSavedViews(
			scope,
			savedViews(scope).filter(searchContext => searchContext.id !== id)
		)
		if (activeIdsByScope.value[scope] === id) setActiveId(scope, null)
	}

	return {
		isHuntingSavedViewOnProject,
		loading,
		setSortOptions,
		sortLabel,
		savedViews,
		activeSavedView,
		presetViews,
		activePresetView,
		currentView,
		viewOf,
		isModified,
		fetchSavedViews,
		applySavedView,
		applyPresetView,
		applyDefaultView,
		discardChanges,
		createSavedView,
		saveActiveSavedView,
		saveActivePresetView,
		renamePresetView,
		renameSavedView,
		deleteSavedView,
	}
})
